import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import NewsArticleBody from "@/components/news/NewsArticleBody";
import { postRowToArticle, type NewsArticle } from "@/lib/posts";
import { useLanguage } from "@/i18n/useLanguage";
import { toast } from "@/hooks/use-toast";
import type { Database } from "@/integrations/supabase/types";

type PostRow = Database["public"]["Tables"]["posts"]["Row"];

/**
 * Admin-only preview of a post (draft or published) rendered with the exact
 * same component the public site uses. Loads by id (not slug) so drafts —
 * which RLS hides from anon — are visible to admins through their session.
 */
const PostPreview = () => {
  const { id } = useParams<{ id: string }>();
  const { localize } = useLanguage();
  const [row, setRow] = useState<PostRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let active = true;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("posts")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (!active) return;
      if (error || !data) {
        toast({ title: "Post not found", description: error?.message, variant: "destructive" });
      }
      setRow((data as PostRow) ?? null);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const article: NewsArticle | null = row
    ? postRowToArticle(row, localize(`/news/${row.slug}`))
    : null;

  const isPublished = row?.status === "published";
  const livePath = row ? localize(`/news/${row.slug}`) : "";

  return (
    <>
      <Helmet>
        <title>Preview · {row?.title ?? "Post"} · Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="sticky top-0 z-50 border-b border-amber-500/30 bg-amber-500/10 backdrop-blur supports-[backdrop-filter]:bg-amber-500/10">
        <div className="container mx-auto px-5 md:px-6 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-amber-500/20 text-amber-200 px-2 py-0.5 text-xs font-medium uppercase tracking-wider">
              Preview
            </span>
            <span className="text-muted-foreground">
              {isPublished ? "Published post" : "Draft — not visible to the public"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm">
              <Link to={`/admin/posts/${id}`}>
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to editor
              </Link>
            </Button>
            {isPublished && (
              <Button asChild variant="outline" size="sm">
                <a href={livePath} target="_blank" rel="noopener noreferrer">
                  Open live page <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-screen bg-background flex flex-col">
        {loading || !article ? (
          <div className="container mx-auto px-5 md:px-6 py-12 max-w-3xl">
            <Skeleton className="h-8 w-2/3 mb-6" />
            <Skeleton className="h-64 w-full mb-6 rounded-xl" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-5/6 mb-2" />
            <Skeleton className="h-4 w-4/6" />
          </div>
        ) : (
          <NewsArticleBody article={article} showBackLinks={false} />
        )}
      </div>
    </>
  );
};

export default PostPreview;
